import React from "react";

import cx from "classnames";

import animationData from "~/common/Star/animations/star.json";

import type { ThumbChoices } from "../ResponseForm/validation";
import { ReactComponent as ThumbDownOffDark } from "./css/thumb-down-off-dark.svg";
import { ReactComponent as ThumbDownOff } from "./css/thumb-down-off.svg";
import { ReactComponent as ThumbDownOnDark } from "./css/thumb-down-on-dark.svg";
import { ReactComponent as ThumbDownOn } from "./css/thumb-down-on.svg";
import { ReactComponent as ThumbUpOffDark } from "./css/thumb-up-off-dark.svg";
import { ReactComponent as ThumbUpOff } from "./css/thumb-up-off.svg";
import { ReactComponent as ThumbUpOnDark } from "./css/thumb-up-on-dark.svg";
import { ReactComponent as ThumbUpOn } from "./css/thumb-up-on.svg";
import RangeInput, { type RangeInputConsumerProps, type RangeInputItemProps } from "./RangeInput";

import styles from "./css/ThumbInput.module.css";

const THUMB_VALUES = ["none", "up", "down"] as const;
export type ThumbValue = (typeof THUMB_VALUES)[number];

const valueToChoiceMap: { [k in Exclude<ThumbValue, "none">]: ThumbChoices[number] } = {
	up: { label: "Great!", value: 10 },
	down: { label: "Not Great", value: 0 },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isThumbValue(value: any): value is ThumbValue {
	return THUMB_VALUES.includes(value);
}

interface BaseThumbProps {
	selected?: boolean;
	readonly?: boolean;
	value: ThumbValue;
}

const THUMBS: { [k in ThumbValue]: [typeof ThumbDownOff, typeof ThumbDownOn] } = {
	none: [ThumbDownOff, ThumbUpOn],
	up: [ThumbUpOff, ThumbUpOn],
	down: [ThumbDownOff, ThumbDownOn],
};

const THUMBS_DARK: { [k in ThumbValue]: [typeof ThumbDownOff, typeof ThumbDownOn] } = {
	none: [ThumbDownOffDark, ThumbUpOnDark],
	up: [ThumbUpOffDark, ThumbUpOnDark],
	down: [ThumbDownOffDark, ThumbDownOnDark],
};

type ThumbProps = BaseThumbProps & Omit<React.SVGProps<SVGSVGElement>, keyof BaseThumbProps>;
export function Thumb({ selected, value, readonly, ...svgProps }: ThumbProps) {
	const html = document.documentElement;
	const Component = html.dataset.theme == `theme-dark` ? THUMBS_DARK[value][+!!selected] : THUMBS[value][+!!selected];
	return (
		<Component
			className={cx({
				selected: selected,
				readonly: readonly,
			})}
			{...svgProps}
		/>
	);
}

type ThumbWrapperProps = RangeInputItemProps<ThumbValue>;

function ThumbWrapper({ selected, value, readonly, hoveredValue }: ThumbWrapperProps) {
	return <Thumb value={value} readonly={readonly} selected={selected || hoveredValue === value} />;
}

export default function ThumbInput(props: RangeInputConsumerProps<ThumbValue>) {
	return (
		<RangeInput<ThumbValue>
			options={["up", "down"]}
			itemComponent={ThumbWrapper}
			animationOptions={{
				animationData,
				rendererSettings: {
					className: styles.lottie,
				},
			}}
			lottieProps={{ width: "1em", height: "1em" }}
			{...props}
		/>
	);
}

ThumbInput.valueToChoiceMap = valueToChoiceMap;
